import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Button } from '@amzn/stencil-react-components/button';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { Input, InputWrapper } from '@amzn/stencil-react-components/input';
import { Select } from '@amzn/stencil-react-components/select';
import { Radio } from '@amzn/stencil-react-components/radio';
import { Label, H2, H3, Text } from '@amzn/stencil-react-components/text';
import { Spacer } from '@amzn/stencil-react-components/layout';
import { View } from '@amzn/stencil-react-components/layout';
import { BY_RULE_FILTERS, BY_TEAM_PERMISSIONS } from '../constants/recipients';
import RuleFilters from '../components/molecules/RuleFilters';
import {
  createSegmentWithRules,
  createSegmentFromS3,
  createSegmentFromFile,
} from '../actions/nudgeActions';
import formatRuleFilters from '../util/segmentCreation/formatRuleFilters';
import { updateRuleFilters } from '../actions/ruleFiltersActions';
import {
  DEFAULT_SEGMENT_NAME_TEXT,
  ERROR_SEGMENT_NAME_TEXT,
  DEFAULT_S3_URL_TEXT,
  ERROR_S3_URL_TEXT,
  SOMETHING_WENT_WRONG,
} from '../constants/messages';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  MessageType,
  SegmentType,
  UploadType,
  S3URLPattern,
  DEFAULT_NOTIFICATION_TIMEOUT_MS,
} from '../constants/createSegment';
import { getFileDetails, removeFileDetails, updateSegment } from '../actions/createSegmentActions';
import ButtonWithLoader from './atoms/ButtonWithLoader';
import styled from '@emotion/styled';
import { HOME_PAGE, SEGMENT_PAGE } from '../constants/routesConstant';
import { useHistory } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import { ResponseStatus } from '../enums/responseStatus';
import { SegmentSection } from './campaign/SegmentSection';
import { updateCampaignForm } from '../actions/createScheduleAction';
import {
  CampaignFormSection,
  MAX_ALLOWED_DESCRIPTION_LENGTH,
  MAX_ALLOWED_NAME_LENGTH,
} from '../enums/schedulingConstant';
import DownloadTemplate from './molecules/DownloadTemplate';
import { displayNotification } from '../actions/notificationActions';
import useRuleFilter from '../hooks/useRuleFilter';
import {
  createSegmentSelector,
  currentSegmentDataSelector,
  ruleFiltersSelector,
} from '../selectors/selectors';

import { IconCrossSmall } from '@amzn/stencil-react-icons';
import { IconUploadSmall } from '@amzn/stencil-react-icons';

const CreateSegment = ({
  getFileDetailsAction,
  removeFileDetailAction,
  userInfo,
  initializeData,
  createSegmentData,
  campaignView,
  showUseSegment,
  onNext
}) => {
  const { formatMessage } = useIntl();
  const { createSegmentResponse: segmentData, updateSegmentStatus } = useSelector(
    createSegmentSelector,
  );
  const { segmentName: segName, segmentDescription: segDescription, segmentId } = useSelector(
    currentSegmentDataSelector,
  );
  const dispatch = useDispatch();
  const { resetSegmentForm } = useRuleFilter();
  const { editMode } = useSelector(createSegmentSelector);
  const { createSegmentWithFile: fileContent } = createSegmentData;
  const [selectedSegmentType, setSelectedSegmentType] = useState(SegmentType.BUILD);
  const [segmentName, setSegmentName] = useState(segName);
  const [segmentWarning, setSegmentWarning] = useState(false);
  const [segmentDescription, setSegmentDescription] = useState(segDescription);
  const ruleFiltersObj = useSelector(ruleFiltersSelector);
  const [s3Url, setS3Url] = useState('');
  const [s3UrlWarning, setS3UrlWarning] = useState(false);
  const history = useHistory();
  const IdentifyRecipientsOptions = ['By rule filters'];

  const [selectedIdentifyRecipients, setIdentifyRecipients] = useState(
    IdentifyRecipientsOptions[0],
  );

  const [selectedUploadType, setUploadType] = useState(UploadType.FIELD);

  const [segmentNameFooterText, setSegmentNameFooterText] = useState(DEFAULT_SEGMENT_NAME_TEXT);
  const [s3UrlFooterText, setS3UrlFooterText] = useState(DEFAULT_S3_URL_TEXT);

  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { isValid } = useRuleFilter();

  const isFormValid =
    selectedSegmentType === SegmentType.BUILD
      ? !isEmpty(segmentName) && !isEmpty(segmentDescription) && isValid
      : true;

  useEffect(
    () => {
      (async () => {
        await dispatch(
          updateCampaignForm(CampaignFormSection.SEGMENT, {
            segmentType: selectedSegmentType,
          }),
        );
      })();
    },
    [selectedSegmentType],
  );

  const createSegmentUsingS3 = async () => {
    if (validateInput()) {
      if (!checkS3UrlPattern(s3Url)) {
        setS3UrlWarning(true);
        setS3UrlFooterText(ERROR_S3_URL_TEXT);
        return;
      }
      setLoading(true);
      try {
        await dispatch(
          createSegmentFromS3(
            {
              userName: userInfo.user,
              userGroupName: userInfo.selectedAccessGroupName,
              segmentName,
              s3Url,
              segmentDescription,
            },
            userInfo.idToken,
          ),
        );
        setLoading(false);
        onNext();
      } catch (e) {
        displayError();
        setLoading(false);
      }
    }
  };

  const createSegmentUsingRules = async () => {
    setLoading(true);
    if (segmentName.trim() !== '') {
      setSegmentWarning(false);
      if (hasSelectedByRulesFilter) {
        const formattedRules = formatRuleFilters(ruleFiltersObj, initializeData);
        await dispatch(
          createSegmentWithRules(
            {
              userName: userInfo.user,
              segmentName,
              segmentDescription,
              userGroupName: userInfo.selectedAccessGroupName,
              ruleDetails: formattedRules,
              editMode: false,
            },
            userInfo.idToken,
          ),
        );
       onNext();
      }
    } else {
      setSegmentWarning(true);
    }
    setLoading(false);
  };

  useEffect(
    () => {
      if (segmentData?.status === ResponseStatus.ERROR) {
        displayError(segmentData?.data?.message);
      } else if (segmentData?.status === ResponseStatus.SUCCESS) {
        displaySuccess();
        if (!campaignView) {
          history.push(SEGMENT_PAGE);
        }
      }
    },
    [segmentData?.status, segmentData?.data],
  );

  useEffect(
    () => {
      if (updateSegmentStatus === ResponseStatus.SUCCESS) {
        history.push(SEGMENT_PAGE);
      }
    },
    [updateSegmentStatus],
  );

  const createSegmentUsingFileUpload = async () => {
    if (segmentName?.trim() === '' || fileContent?.content?.trim() === '') {
      validateSegmentName();
      return;
    }
    setLoading(true);
    try {
      const segmentDetails = {
        userName: userInfo.user,
        userGroupName: userInfo.selectedAccessGroupName,
        segmentName,
        segmentDataAsString: fileContent?.content ?? '',
        segmentDescription,
      };
      await dispatch(createSegmentFromFile(segmentDetails, userInfo.idToken));
      setLoading(false);
      onNext();
    } catch (e) {
      displayError();
      setLoading(false);
    }
  };

  const displaySuccess = () => {
    dispatch(
      displayNotification({
        type: selectedUploadType === UploadType.FIELD ? MessageType.INFO : MessageType.SUCCESS,
        message:
          selectedUploadType === UploadType.FIELD
            ? formatMessage({ id: 'app.createSegment.message.rulesSegment' })
            : formatMessage({ id: 'app.createSegment.message.success' }),
        timeout: selectedUploadType === UploadType.FIELD ? 20000 : DEFAULT_NOTIFICATION_TIMEOUT_MS,
      }),
    );
  };

  const displayError = message => {
    dispatch(
      displayNotification({
        type: MessageType.ERROR,
        message: message ?? SOMETHING_WENT_WRONG,
        timeout: DEFAULT_NOTIFICATION_TIMEOUT_MS,
      }),
    );
  };

  const onSubmit = () => {
    setIsSubmitted(true);
    switch (selectedUploadType) {
      case UploadType.S3: {
        createSegmentUsingS3();
        break;
      }
      case UploadType.FIELD: {
        createSegmentUsingRules();
        break;
      }
      case UploadType.FILE: {
        createSegmentUsingFileUpload();
        break;
      }
      default: {
        createSegmentUsingRules();
      }
    }
  };

  const validateInput = () => {
    validateS3Url();
    validateSegmentName();
    return !(s3UrlWarning && segmentWarning);
  };

  const validateSegmentName = () => {
    if (segmentName === undefined || segmentName?.trim() === '') {
      setSegmentWarning(true);
      setSegmentNameFooterText(ERROR_SEGMENT_NAME_TEXT);
    } else {
      setSegmentWarning(false);
      setSegmentNameFooterText(DEFAULT_SEGMENT_NAME_TEXT);
    }
  };

  const validateS3Url = () => {
    if (s3Url === undefined || s3Url?.trim() === '') {
      setS3UrlWarning(true);
      setS3UrlFooterText(ERROR_S3_URL_TEXT);
    } else {
      setS3UrlWarning(false);
      setS3UrlFooterText(DEFAULT_S3_URL_TEXT);
    }
  };

  const checkS3UrlPattern = url => {
    return S3URLPattern.test(url);
  };

  const getFileDetails = async () => {
    const file = document.querySelector('#file_upload')['files'][0];
    await getFileDetailsAction(file);
  };

  const onFileSelectClick = async () => {
    const element = document.getElementById('file_upload');
    element.click();
    await getFileDetails();
  };

  const removeFileSelection = async () => {
    await removeFileDetailAction();
  };

  const onUpdateSegment = async () => {
    setLoading(true);
    const formattedRules = formatRuleFilters(ruleFiltersObj, initializeData);
    const payload = {
      segment: {
        userName: userInfo.user,
        segmentDescription,
        userGroupName: userInfo.selectedAccessGroupName,
        ruleDetails: formattedRules,
        segmentIdentifier: {
          segmentName,
          segmentDescription,
          segmentId,
        },
        editMode: true,
      },
      userName: userInfo.user,
      userGroupName: userInfo.selectedAccessGroupName,
    };
    await dispatch(updateSegment(payload, userInfo.idToken));
    setLoading(false);
  };

  // clean up function
  useEffect(() => {
    return () => {
      removeFileSelection();
      resetSegmentForm();
    };
  }, []);

  // View for upload file
  const UploadFileView = styled(View)({
    border: '3px dashed #EEF5F6',
    '&:after': {
      content: '"Drop files here"',
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
      verticalAlign: 'middle',
      height: '100px',
      color: '#d4d8d8',
    },
  });

  // View for display uploaded file
  const UploadedFileView = styled(View)({
    border: '3px dashed #EEF5F6',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100px',
  });

  const hasSelectedByRulesFilter = selectedIdentifyRecipients === BY_RULE_FILTERS;
  const hasSelectedByTeamPermission = selectedIdentifyRecipients === BY_TEAM_PERMISSIONS;

  return (
    <React.Fragment>
      <Col width={'98%'} flex="1" gridGap={'S300'} data-testid="create-segment">
        <H2 css={{fontSize: '2.5rem', lineHeight: '1.3', fontWeight: '400'}}>
          <FormattedMessage
            id="app.createSegment.createSegmentLabel"
            defaultMessage="Create a Segment"
          />
        </H2>
        <Row gridGap="S400">
          <Row
            className={`segment-type-wrapper ${
              selectedSegmentType === SegmentType.BUILD ? 'selected' : ''
            }`}
            gridGap={'S400'}
            backgroundColor="neutral0"
            padding="S300"
            width={'30%'}
          >
            <Col>
              <Label fontSize={'T300'} htmlFor="build-segment-radio">
                <FormattedMessage
                  id="app.createSegment.build.segmentLabel"
                  defaultMessage="Build a segment"
                />
              </Label>
              <Text fontSize={'T100'}>
                <FormattedMessage
                  id="app.createSegment.build.dynamicSegmentText"
                  defaultMessage="Builds a dynamic segment based on attributes of your customers"
                />
              </Text>
            </Col>
            <Col>
              <Radio
                name="segment-type"
                id="build-segment-radio"
                data-testid="build-radio-option"
                value="build"
                checked={selectedSegmentType === SegmentType.BUILD}
                onChange={isChecked => {
                  if (isChecked?.target?.checked) {
                    setUploadType(UploadType.FIELD);
                    setSelectedSegmentType(SegmentType.BUILD);
                  }
                }}
              />
            </Col>
          </Row>
          <Row
            className={`segment-type-wrapper ${
              selectedSegmentType === SegmentType.IMPORT ? 'selected' : ''
            }`}
            gridGap={8}
            backgroundColor="neutral0"
            padding="S300"
            width={'30%'}
          >
            <Col>
              <Label fontSize={'T300'} htmlFor="import-segment-radio">
                <FormattedMessage
                  id="app.createSegment.import.segmentLabel"
                  defaultMessage="Import a Segment"
                />
              </Label>
              <Text fontSize={'T100'}>
                <FormattedMessage
                  id="app.createSegment.import.importSegmentText"
                  defaultMessage="Import a .csv or .json file that contains a list of specific recipients"
                />
              </Text>
            </Col>
            <Col>
              <Radio
                name="segment-type"
                id="import-segment-radio"
                data-testid="import-radio-option-import"
                value="import"
                checked={selectedSegmentType === SegmentType.IMPORT}
                onChange={isChecked => {
                  if (isChecked.target.checked) {
                    setUploadType(UploadType.FILE);
                    setSelectedSegmentType(SegmentType.IMPORT);
                  }
                }}
              />
            </Col>
          </Row>
          {showUseSegment && (
            <Row
              className={`segment-type-wrapper ${
                selectedSegmentType === SegmentType.EXISTING ? 'selected' : ''
              }`}
              gridGap={8}
              backgroundColor="neutral0"
              padding="S300"
              width={'30%'}
            >
              <Col>
                <Label fontSize={'T300'} htmlFor="import-segment-radio">
                  <FormattedMessage
                    id="app.createSegment.build.useExistingSegment"
                    defaultMessage="Use existing segment"
                  />
                </Label>
                <Text fontSize={'T100'}>
                  <FormattedMessage
                    id="app.createSegment.build.useExistingSegmentText"
                    defaultMessage="Use an already existing segment"
                  />
                </Text>
              </Col>
              <Col>
                <Radio
                  name="segment-type"
                  id="import-segment-radio"
                  data-testid="import-radio-option-existing"
                  value="import"
                  checked={selectedSegmentType === SegmentType.EXISTING}
                  onChange={isChecked => {
                    if (isChecked?.target?.checked) {
                      setSelectedSegmentType(SegmentType.EXISTING);
                    }
                  }}
                />
              </Col>
            </Row>
          )}
        </Row>
        {selectedSegmentType !== SegmentType.EXISTING && (
          <>
            <Row>
              <Text fontSize={'T100'} style={{ fontWeight: 'bolder' }} color="primary">
                <FormattedMessage
                  id="app.createSegment.build.mandatoryFields"
                  defaultMessage="* Mandatory Fields"
                />
              </Text>
            </Row>
            <Row display="flex" flexDirection="column" gridGap={20}>
              <View width={'60%'} height={64}>
                <InputWrapper
                  labelText=""
                  id="segment-name-wrapper"
                  error={segmentWarning}
                  footer={segmentNameFooterText}
                >
                  {inputProps => (
                    <>
                      <Label
                        fontSize={'T300'}
                        display="flex"
                        htmlFor="rule-filter-1"
                        id="rule-filter-label1"
                      >
                        <Text
                          style={{
                            justifyContent: 'center',
                            alignItems: 'center',
                            display: 'flex',
                          }}
                        >
                          <FormattedMessage
                            id="app.createSegment.build.segmentName"
                            defaultMessage="Segment Name"
                          />
                        </Text>
                        <Text fontSize={'T100'} style={{ fontWeight: 'bolder' }} color="primary">
                          *
                        </Text>
                      </Label>
                      <Input
                        {...inputProps}
                        data-testid="segment-name-input"
                        error={segmentWarning}
                        value={segmentName}
                        maxLength={MAX_ALLOWED_NAME_LENGTH}
                        onChange={e => {
                          setSegmentName(e.target.value);
                          validateSegmentName();
                        }}
                      />
                    </>
                  )}
                </InputWrapper>
              </View>
              <Spacer height={32} />
              <View width={'60%'} height={64}>
                <InputWrapper id="segment-description-wrapper" labelText="">
                  {inputProps => (
                    <>
                      <Label
                        fontSize={'T300'}
                        display="flex"
                        htmlFor="rule-filter-1"
                        id="rule-filter-label1"
                      >
                        <Text
                          style={{
                            justifyContent: 'center',
                            alignItems: 'center',
                            display: 'flex',
                          }}
                        >
                          <FormattedMessage
                            id="app.createSegment.build.segmentDescription"
                            defaultMessage="Segment Description"
                          />
                        </Text>
                        <Text fontSize={'T100'} style={{ fontWeight: 'bolder' }} color="primary">
                          *
                        </Text>
                      </Label>
                      <Input
                        {...inputProps}
                        value={segmentDescription}
                        maxLength={MAX_ALLOWED_DESCRIPTION_LENGTH}
                        data-testid="segment-description-input"
                        onChange={e => {
                          setSegmentDescription(e.target.value);
                        }}
                      />
                    </>
                  )}
                </InputWrapper>
              </View>
              <Spacer height={32} />
            </Row>
          </>
        )}
        <H3>
          <FormattedMessage
            id="app.createSegment.identifyRecipient"
            defaultMessage="Identify recipients"
          />
        </H3>
        {selectedSegmentType === SegmentType.EXISTING && <SegmentSection />}
        {selectedSegmentType === SegmentType.BUILD && (
          <View width={'100%'} height={64}>
            <Row gridGap="S300" alignItems="flex-end">
              <Col width={'50%'}>
                <Label
                  htmlFor="identify-recipients-type-select"
                  data-testid="build-segment-label"
                  id="some-select-label"
                >
                  <FormattedMessage
                    id="app.createSegment.identifyRecipient"
                    defaultMessage="Identify recipients"
                  />
                </Label>
                <Select
                  id="identify-recipients-type-select"
                  labelId="some-select-label"
                  onChange={val => {
                    setIdentifyRecipients(val);
                  }}
                  options={IdentifyRecipientsOptions}
                  value={selectedIdentifyRecipients}
                />
              </Col>
              <Col width={'50%'}>
                {hasSelectedByTeamPermission && (
                  <Input
                    id="team-name-input"
                    name="team-name-input"
                    placeholder="Provide team name, LDAP or POSIX group"
                  />
                )}
              </Col>
            </Row>
          </View>
        )}
        {selectedSegmentType === SegmentType.BUILD &&
          hasSelectedByRulesFilter && (
            <View width={'100%'}>
              <Spacer height={32} />
              <RuleFilters />
            </View>
          )}
        {selectedSegmentType === SegmentType.IMPORT && (
          <View width={'100%'} flexDirection="column" padding={'30px 0px 0px 0px'}>
            <Row gridGap="S400">
              <Col width={'30%'}>
                <Row gridGap="S400" alignItems="center">
                  <Radio
                    name="segment-import-type"
                    id="import-segment-upload-file-radio"
                    value="import-upload-file"
                    checked={selectedUploadType === UploadType.FILE}
                    onChange={isChecked => {
                      if (isChecked) {
                        setUploadType('file');
                      }
                    }}
                  />
                  <Label data-testid="upload-file-label" htmlFor="import-segment-upload-file-radio">
                    <FormattedMessage
                      id="app.createSegment.upload.uploadSegmentText"
                      defaultMessage="Upload files from your computer"
                    />
                  </Label>
                </Row>
              </Col>
              <Col width={'30%'}>
                <Row gridGap="S400" alignItems="center">
                  <Radio
                    name="segment-import-type"
                    data-testid="import-segment-s3-bucket-radio"
                    value="import-s3-bucket"
                    checked={selectedUploadType === UploadType.S3}
                    onChange={isChecked => {
                      if (isChecked) {
                        setUploadType(UploadType.S3);
                      }
                    }}
                  />
                  <Label htmlFor="import-segment-upload-file-radio">
                    <FormattedMessage
                      id="app.createSegment.import.s3BucketImport"
                      defaultMessage="Import files from Amazon S3 bucket"
                    />
                  </Label>
                </Row>
              </Col>
            </Row>
            {selectedUploadType === UploadType.FILE && (
              <Col gridGap="S200">
                <Text fontSize="T200">
                  <FormattedMessage
                    id="app.createSegment.import.filesToImport"
                    defaultMessage="Files to import"
                  />
                </Text>
                <Text fontSize="T100">
                  {' '}
                  <FormattedMessage
                    id="app.createSegment.import.importFilesText"
                    defaultMessage="Choose file."
                  />
                </Text>

                {fileContent?.isUploaded ? (
                  <UploadedFileView>
                    {fileContent?.isUploaded && (
                      <Row alignItems="center" onClick={removeFileSelection}>
                        {fileContent?.fileName}
                        <IconCrossSmall style={{ cursor: 'pointer' }} aria-hidden={true} />
                      </Row>
                    )}
                  </UploadedFileView>
                ) : (
                  <UploadFileView
                    className={isSubmitted && isEmpty(fileContent?.content) ? 'error' : ''}
                  >
                    {fileContent?.isUploaded && (
                      <Row alignItems="center" onClick={removeFileSelection}>
                        {fileContent?.fileName}
                        <IconCrossSmall style={{ cursor: 'pointer' }} aria-hidden={true} />
                      </Row>
                    )}
                  </UploadFileView>
                )}
                {isSubmitted &&
                  isEmpty(fileContent?.content) && (
                    <Text className="errorText">
                      <FormattedMessage
                        id="app.createSegment.import.fileUploadErrorMessage"
                        defaultMessage="File is empty/invalid. Please upload a valid csv file."
                      />
                    </Text>
                  )}
                <Text>
                  <FormattedMessage
                    id="app.createSegment.import.orChooseBelow"
                    defaultMessage="Or Choose below"
                  />
                </Text>
                <Row gridGap="S300">
                  <Col width="30%">
                    <input
                      type="file"
                      style={{ display: 'none' }}
                      id="file_upload"
                      data-testid="file-upload-input"
                      onChange={getFileDetails}
                      accept={'.csv'}
                    />
                    <Button
                      hasIcon
                      tertiary
                      onClick={onFileSelectClick}
                      disabled={fileContent?.isUploaded}
                    >
                      <Row gridGap="S200" alignItems="center">
                        <IconUploadSmall
                          title={formatMessage({ id: 'app.createSegment.chooseFileLabel' })}
                          aria-hidden={true}
                        />
                        <FormattedMessage
                          id="app.createSegment.chooseFileLabel"
                          defaultMessage="Choose File"
                        />
                      </Row>
                    </Button>
                  </Col>
                  <Col width="30%">
                    <View display="flex" alignItems="center">
                      <DownloadTemplate />
                    </View>
                  </Col>
                </Row>
              </Col>
            )}
            {selectedUploadType === UploadType.S3 && (
              <Col>
                <View width={'60%'} height={64}>
                  <InputWrapper
                    id="s3-url-input-wrapper"
                    labelText={formatMessage({ id: 'app.createSegment.amazonS3Url' })}
                    error={s3UrlWarning}
                    footer={s3UrlFooterText}
                  >
                    {inputProps => (
                      <Input
                        {...inputProps}
                        data-testid="s3-url-input"
                        placeholder="s3://[Bucket Name]/[File]"
                        error={s3UrlWarning}
                        value={s3Url}
                        onChange={e => {
                          setS3Url(e.target.value);
                          validateS3Url();
                        }}
                      />
                    )}
                  </InputWrapper>
                </View>
              </Col>
            )}
          </View>
        )}
        <Spacer height={50} width={50} />

        {selectedSegmentType !== SegmentType.EXISTING && (
          <>
            <Row gridGap={'m200'}>
              {editMode ? (
                <ButtonWithLoader
                  data-testid="update-segment-btn"
                  isLoading={loading}
                  isDisabled={!isFormValid}
                  isPrimary={true}
                  onClickHandler={onUpdateSegment}
                >
                  <FormattedMessage
                    id="app.createSegment.button.updateSegment"
                    defaultMessage="Update Segment"
                  />
                </ButtonWithLoader>
              ) : (
                <ButtonWithLoader
                  data-testid="save-continue-btn"
                  isLoading={loading}
                  isDisabled={!isFormValid}
                  isPrimary={true}
                  onClickHandler={onSubmit}
                >
                  <FormattedMessage
                    id="app.createSegment.button.saveAndContinue"
                    defaultMessage="Save and continue"
                  />
                </ButtonWithLoader>
              )}
              <Spacer width={50} />
              <Button
                data-testid="cancel-button"
                onClick={() => history.push(HOME_PAGE)}
                tertiary={true}
              >
                <FormattedMessage id="app.createSegment.button.cancel" defaultMessage="Cancel" />
              </Button>
            </Row>
            <Spacer width="20px" />
          </>
        )}
      </Col>
    </React.Fragment>
  );
};

CreateSegment.defaultProps = {
  campaignView: false,
  showUseSegment: false,
};

CreateSegment.propTypes = {
  userInfo: PropTypes.object.isRequired,
  ruleFilters: PropTypes.object.isRequired,
  initializeData: PropTypes.object.isRequired,
  updateRuleFiltersAction: PropTypes.func.isRequired,
  campaignView: PropTypes.bool,
};

const mapStateToProps = state => ({
  userInfo: { ...state.userInfo },
  ruleFilters: { ...state.ruleFilters },
  initializeData: { ...state.initializeData },
  createSegmentData: { ...state.createSegment },
  createSegmentWithFile: { ...state.createSegmentWithFile },
});

const mapDispatchToProps = {
  updateRuleFiltersAction: updateRuleFilters,
  getFileDetailsAction: getFileDetails,
  removeFileDetailAction: removeFileDetails,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CreateSegment);
